// DOM-free Parallel Sets layout: data + configuration + plot size -> ribbon polygons.
// Reproduces the coordinates of lib/d3.parsets.js (buildTree and layout) exactly; d3.parsets keeps rendering,
// the metrics are computed from this function.
import { normalizeConfig } from '../config.js';

export const DEFAULT_LAYOUT_OPTIONS = { width: 960, height: 600, spacing: 20 };

// Vertical position of the first axis and margin below the last one, as in d3.parsets.
const Y0 = 45;
const BOTTOM_MARGIN = 2;

function buildTree(rows, attributeNames) {
    const root = { children: new Map(), count: 0 };
    const last = attributeNames.length - 1;
    for (const row of rows) {
        let node = root;
        for (let j = 0; j <= last; j++) {
            const category = row[attributeNames[j]];
            node.count++;
            let child = node.children.get(category);
            if (child === undefined) {
                child = { children: j === last ? null : new Map(), count: 0 };
                node.children.set(category, child);
            }
            node = child;
        }
        node.count++;
    }
    return root;
}

/**
 * @param dataset        { rows } as returned by the dataset loader
 * @param config         [{ name, categories }] (attribute order and category orders)
 * @param layoutOptions  { width, height, spacing }
 * @returns { width, height, total, attributes, ribbons }
 *   attributes: [{ name, y, categories: [{ name, count, x, dx }] }], categories without rows are dropped
 *   ribbons:    in depth-first order, each { band, path, count, dx, tl, bl, br, tr } with points [x, y];
 *               band i lies between attribute i and i + 1; tl/tr are on the upper axis, bl/br on the lower one
 */
export function computeLayout(dataset, config, layoutOptions = {}) {
    const { width, height, spacing } = { ...DEFAULT_LAYOUT_OPTIONS, ...layoutOptions };
    const attributes = normalizeConfig(config).map(a => ({
        name: a.name,
        categories: a.categories.map(name => ({ name, count: 0 })),
    }));
    const nd = attributes.length;
    const tree = buildTree(dataset.rows, attributes.map(a => a.name));
    const dy = (height - Y0 - BOTTOM_MARGIN) / (nd - 1);
    attributes.forEach((a, i) => { a.y = Y0 + i * dy; });

    // Per-category counts; rows with a category that is not part of the configuration are ignored.
    const total = (function rollup(node, i) {
        if (!node.children) return node.count;
        let sum = 0;
        attributes[i].categories.forEach(c => {
            const child = node.children.get(c.name);
            if (!child) return;
            const count = rollup(child, i + 1);
            c.count += count;
            sum += count;
        });
        return sum;
    })(tree, 0);

    // Stack the categories on their axis.
    const offsets = attributes.map(a => {
        a.categories = a.categories.filter(c => c.count);
        let x = 0;
        const padding = spacing / (a.categories.length - 1);
        a.categories.forEach(c => {
            c.x = x;
            c.dx = c.count / total * (width - spacing);
            x += c.dx + padding;
        });
        return a.categories.map(() => ({ in: 0, out: 0 }));
    });

    const ribbons = [];
    function recurse(parentIndex, node, path, depth) {
        const attribute = attributes[depth];
        const parent = attributes[depth - 1].categories[parentIndex];
        const parentOffset = offsets[depth - 1][parentIndex];
        attribute.categories.forEach((c, index) => {
            const child = node.children.get(c.name);
            if (!child) return;
            const dx = child.count / total * (width - spacing);
            const tx = c.x + offsets[depth][index].in;
            offsets[depth][index].in += dx;
            const sx = parent.x + parentOffset.out;
            parentOffset.out += dx;
            const sy = attributes[depth - 1].y;
            const ty = attribute.y;
            const childPath = path.concat(c.name);
            ribbons.push({
                band: depth - 1,
                path: childPath,
                count: child.count,
                dx,
                tl: [sx, sy],
                bl: [tx, ty],
                br: [tx + dx, ty],
                tr: [sx + dx, sy],
            });
            if (depth + 1 < nd) recurse(index, child, childPath, depth + 1);
        });
    }
    attributes[0].categories.forEach((c, index) => {
        const child = tree.children.get(c.name);
        if (child) recurse(index, child, [c.name], 1);
    });

    return { width, height, total, attributes, ribbons };
}
